import { CircularProgress, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import { forwardRef, useCallback, useContext, useEffect } from "react";
import { useState } from "react";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  SafetyDashboardApiFactory,
  HarshEventsBreakdownDto,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { WidgetProps } from "../../../assets/maintenance-dashboard/common";
import { Widget } from "../../../assets/maintenance-dashboard/widgets/Widget";
import { SelectedDriversFilterContext } from "../common";
import styles from "./HarshEventsBreakdown.module.css";

const useStyles = makeStyles((theme: Theme) => ({
  red: {
    color: theme.palette.error.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.error.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
  yellow: {
    color: theme.palette.warning.main,
    cursor: "pointer",
    "&:hover": {
      textShadow: `0px 0px 2px ${theme.palette.warning.main}, 0 0 1em rgba(255, 255, 255, 0.25), 0 0 0.2em rgba(255, 255, 255, 0.5)`,
    },
  },
}));

export const HarshEventsBreakdown = forwardRef(
  ({ className, icon, title, ...rest }: WidgetProps, ref) => {
    const classes = useStyles();
    const [harshEvents, setHarshEvents] = useState<HarshEventsBreakdownDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const axios = useAxiosInstance();
    const filterContext = useContext(SelectedDriversFilterContext);
    useEffect(() => {
      if (!axios) {
        return;
      }

      setLoading((x) => true);
      SafetyDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppSafetydashboardHarsheventsbreakdownGet()
        .then((result) => {
          setHarshEvents(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios]);

    const handleAccelerationClick = useCallback(() => {
      if (!filterContext || !harshEvents?.harshAcceleration) {
        return;
      }

      filterContext.setSelectedFilter("WITH_HARSH_ACCELERATION");
    }, [filterContext, harshEvents]);

    const handleBrakingClick = useCallback(() => {
      if (!filterContext || !harshEvents?.harshBraking) {
        return;
      }

      filterContext.setSelectedFilter("WITH_HARSH_BRAKING");
    }, [filterContext, harshEvents]);

    const handleTurnsClick = useCallback(() => {
      if (!filterContext || !harshEvents?.harshTurn) {
        return;
      }

      filterContext.setSelectedFilter("WITH_HARSH_TURNS");
    }, [filterContext, harshEvents]);

    const handleImpactClick = useCallback(() => {
      if (!filterContext || !harshEvents?.harshStop) {
        return;
      }

      filterContext.setSelectedFilter("WITH_IMPACTS");
    }, [filterContext, harshEvents]);

    const {
      harshAcceleration,
      harshBraking,
      harshStop,
      harshTurn,
    } = harshEvents
      ? harshEvents
      : { harshAcceleration: 0, harshBraking: 0, harshStop: 0, harshTurn: 0 };
    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className={styles["categoriesWrapper"]}>
          {loading ? (
            <div className={styles.categories}>
              <div className={classNames(styles.category, "category")}>
                <CircularProgress size="3rem" />
              </div>
            </div>
          ) : (
            <>
              <div className={styles.categories}>
                <div className={styles.category}>
                  <div
                    className={`${styles["category-value"]} ${classes.yellow}`}
                    onClick={handleAccelerationClick}
                  >
                    {harshAcceleration}
                  </div>
                  <div className={styles["category-title"]}>
                    Harsh acceleration
                  </div>
                </div>
                <div className={styles.category}>
                  <div
                    className={`${styles["category-value"]} ${classes.red}`}
                    onClick={handleBrakingClick}
                  >
                    {harshBraking}
                  </div>
                  <div className={styles["category-title"]}>Harsh braking</div>
                </div>
              </div>
              <div className={styles.categories}>
                <div className={styles.category}>
                  <div
                    className={`${styles["category-value"]} ${classes.yellow}`}
                    onClick={handleTurnsClick}
                  >
                    {harshTurn}
                  </div>
                  <div className={styles["category-title"]}>
                    Taking harsh turn
                  </div>
                </div>
                <div className={styles.category}>
                  <div
                    className={`${styles["category-value"]} ${classes.red}`}
                    onClick={handleImpactClick}
                  >
                    {harshStop}
                  </div>
                  <div className={styles["category-title"]}>
                    Possible impact
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Widget>
    );
  }
);
